import { useGetPatientAbTestsQuery } from 'src/participant/patientQueries.generated'
import React from 'react'
import { Autosave, CustomTextField } from 'src/form'
import { Box, Card, CardContent, Grid, MenuItem, Typography } from '@mui/material'
import { ApolloError } from '@apollo/client'
import { Field, Form, Formik } from 'formik'
import { AppearanceTypes, useToasts } from 'react-toast-notifications'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import useUpdateABTestAssignments from 'src/legacy/components/ParticipantRecords/ParticipantRecord/ABTests/useUpdateABTestAssignments'
import {
  EMPTY_VALUE,
  getABTestValidationSchema,
} from 'src/legacy/components/ParticipantRecords/ParticipantRecord/ABTests/getABTestValidationSchema'
import { AbTest } from 'src/graphql.generated'

type Props = {
  uid: string
  isAdmin?: boolean
}

export default function ABTestForm({ uid, isAdmin = false }: Props) {
  const { data } = useGetPatientAbTestsQuery({ variables: { uid }, fetchPolicy: 'cache-and-network' })
  const updateABTestAssignments = useUpdateABTestAssignments(uid)
  const { addToast } = useToasts()

  const disableEditing = !isAdmin

  const initialValues = apiValueToFormValue(data?.patient.abTests)
  const validationSchema = getABTestValidationSchema(data?.patient.abTests)

  const onSuccess = () => {
    const successMessage = 'Participant AB test variables updated successfully'
    const toastOptions = {
      // type assertion used, as library typing is out of date. autoDismissTimeout option works, but library typings do not support it in the Options type, thus hacking around it with the "as" assertion
      appearance: 'success' as AppearanceTypes,
      autoDismissTimeout: 3000,
    }
    addToast(successMessage, toastOptions)
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={toFormikValidationSchema(validationSchema)}
      enableReinitialize
      onSubmit={async (values, { setStatus }) => {
        try {
          const { patientABTestsByName } = validationSchema.parse(values)
          const abTests = formValueToApiValue(patientABTestsByName)
          await updateABTestAssignments(abTests)
          onSuccess()
        } catch (error) {
          setStatus({
            error,
            viewOnly: false,
          })

          if (error instanceof ApolloError && error?.graphQLErrors[0]?.extensions?.clientError) {
            addToast(error.message)
          } else {
            addToast('Something went wrong!')
          }
        }
      }}
    >
      {() => (
        <Form>
          <Card style={{ marginTop: 10 }}>
            <Autosave debounceMs={100} />
            <CardContent>
              <Box mb={1} mt={3}>
                <Typography variant="h3">AB Tests</Typography>
              </Box>

              {data?.patient.abTests?.length === 0 && <Typography variant="body1">No active AB tests</Typography>}

              {data?.patient.abTests?.map((test) => (
                <Grid container spacing={2} key={test.name}>
                  <Grid item xs={12} md={6}>
                    {test.description && (
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                        {test.description}
                      </Typography>
                    )}
                    <Field
                      name={`patientABTestsByName.${test.name}`}
                      label={test.name}
                      component={CustomTextField}
                      fullWidth
                      type="select"
                      disabled={disableEditing}
                    >
                      <MenuItem value={EMPTY_VALUE}>
                        <em>(no value)</em>
                      </MenuItem>
                      {test.variants.map((group) => (
                        <MenuItem key={group.name} value={group.name}>
                          {group.name}
                          {group.description && (
                            <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                              — {group.description}
                            </Typography>
                          )}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                </Grid>
              ))}
            </CardContent>
          </Card>
        </Form>
      )}
    </Formik>
  )
}

function apiValueToFormValue(abTests?: Array<AbTest> | null) {
  if (!abTests) return { patientABTestsByName: {} }

  const formValue: Record<string, string> = {}
  abTests.forEach((test) => {
    formValue[test.name] = test.assignedVariant ?? EMPTY_VALUE
  })

  return { patientABTestsByName: formValue }
}

function formValueToApiValue(patientABTestsByName: Record<string, string | null>) {
  return Object.entries(patientABTestsByName).map(([name, assignedVariant]) => ({
    name,
    assignedVariant,
  }))
}
